import * as React from 'react';
import { ButtonGroup, ButtonGroupProps } from '@mui/material';
import { ClickAwayListener } from '@mui/material';
import { Grow } from '@mui/material';
import { Paper } from '@mui/material';
import { Popper } from '@mui/material';
import { MenuItem } from '@mui/material';
import { MenuList } from '@mui/material';
import Button from '../button/button';
import { ArrowDownTwo } from '../icons';
import Typography from '../typography/typography';
import { ButtonProps, CircularProgress, SxProps } from '@mui/material';
import { useLanguage } from '@erpsquad/common/runtime';

export interface IDropdownMenuOption {
	label: string | JSX.Element;
	callback: (event?: any, index?: number) => void;
	color?: string;
	disableToggle?: boolean;
}

interface DropdownButtonProps extends ButtonProps {
	children?: React.ReactNode;
	options: IDropdownMenuOption[];
	isMenuThirdItemDisabled?: boolean;
	isOnlyDropDownButton?: boolean;
	onlyDropDownButtonProps?: {
		style?: SxProps;
	};
	buttonGroupProps?: ButtonGroupProps;
	dropdownButtonProps?: ButtonGroupProps;
	toggleCallback?: (e?: boolean) => void;
	loading?: boolean;
}

const DropdownButton: React.FC<DropdownButtonProps> = ({
	children,
	options,
	isMenuThirdItemDisabled,
	isOnlyDropDownButton = false,
	onlyDropDownButtonProps = null,
	buttonGroupProps = null,
	dropdownButtonProps=null,
	toggleCallback,
	loading = false,
	...rest
}) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);
   const {isRtl}=useLanguage();
	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleClose = (event: Event) => {
		if (
			anchorRef.current &&
			anchorRef.current.contains(event.target as HTMLElement)
		) {
			return;
		}

		setOpen(false);
	};

	React.useEffect(() => {
		toggleCallback?.(open);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	return (
		<React.Fragment>
			<ButtonGroup
				sx={loading ? { boxShadow: 'none' } : isRtl?{flexDirection:'row-reverse'}:{}}
				{...buttonGroupProps}
				ref={anchorRef}
				disabled={loading}>
				{isOnlyDropDownButton ? (
					<Button
						{...rest}
						sx={{
							borderRightColor: '#ffffff20 !important',
							...onlyDropDownButtonProps?.style
						}}
						onClick={handleToggle}>
						{children}
					</Button>
				) : (
					<>
						<Button
							{...rest}
							sx={(theme) => ({
								borderRightColor: `${theme.palette.theme?.secondary[100]} !important`,
								borderRight: '1px solid',
								borderTopRightRadius: 0,
								borderBottomRightRadius: 0,
								...(typeof rest?.sx === 'function'
									? rest.sx(theme)
									: rest?.sx || {})
							})}>
							{children}
						</Button>
						<Button
							size='small'
							aria-controls={open ? 'split-button-menu' : undefined}
							aria-expanded={open ? 'true' : undefined}
							aria-label='select merge strategy'
							aria-haspopup='menu'
							{...dropdownButtonProps}
							sx={(theme) => ({
								p: 1,
								minWidth: 'unset !important',
								borderTopLeftRadius: 0,
								borderBottomLeftRadius: 0,
								...(typeof dropdownButtonProps?.sx === 'function'
									? dropdownButtonProps.sx(theme)
									: dropdownButtonProps?.sx || {})
							})}
							onClick={handleToggle}>
							{loading ? (
								<CircularProgress size={15} color='inherit' />
							) : (
								<ArrowDownTwo fontSize='small' />
							)}
						</Button>
					</>
				)}
			</ButtonGroup>
			<Popper
				sx={{
					zIndex: 1
				}}
				open={open}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin:
								placement === 'bottom' ? 'center top' : 'center bottom'
						}}>
						<Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: '8px', mt:0.25}}>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList id='split-button-menu' autoFocusItem>
									{options.map((option, index) => (
										<MenuItem
											key={index}
											disabled={isMenuThirdItemDisabled ? false : index === 2}
											onClick={(event) => {
												option.callback(event, index);
												if (!option?.disableToggle) {
													handleToggle();
												}
											}} >
											<Typography
												type='s4'
												color={
													option.color ? option.color : 'theme.secondary.1000'
												}
												sx={{
													width: '100%'
												}}>
												{option.label}
											</Typography>
										</MenuItem>
									))}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</React.Fragment>
	);
};

export default DropdownButton;
